import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * AR Aging — FR-ACC-03.06 / 03.07
 * Five-bucket aging (current, 1-30, 31-60, 61-90, 90+) with visual
 * stacked-bar summary + sortable detail table. Dunning runs from here.
 */
export function ArAgingPage() {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);
  const [sortBy, setSortBy] = useState<'total' | 'overdue'>('overdue');

  const agingQuery = useQuery({
    queryKey: ['ar', 'aging', asOfDate],
    queryFn: () => accountingApi.arAging(asOfDate),
  });

  const dunningMut = useMutation({
    mutationFn: () => accountingApi.runDunning(false),
    onSuccess: (d) => {
      toast.success(`Dunning: ${d.sent} sent · ${d.skipped} skipped of ${d.processed} overdue`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['ar'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Receivables"
        title="AR aging"
        subtitle="What customers owe, bucketed by days overdue. Trigger dunning notices to collect."
        actions={
          <button
            className="btn-primary"
            disabled={dunningMut.isPending}
            onClick={() => dunningMut.mutate()}
          >
            {dunningMut.isPending ? 'Running…' : 'Run dunning'}
          </button>
        }
      />

      {/* As-of-date */}
      <div className="card p-4 mb-6 flex gap-4 items-end border-slate-200">
        <div>
          <label className="label">As of date</label>
          <input
            type="date"
            className="input w-44"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
          />
        </div>
        <div className="text-xs text-slate-500 max-w-sm">
          Aging is calculated from each invoice's due date. Bucket names describe days past due.
        </div>
      </div>

      {agingQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : agingQuery.error ? (
        <ErrorState error={agingQuery.error} />
      ) : (
        <AgingBody data={agingQuery.data} sortBy={sortBy} setSortBy={setSortBy} />
      )}
    </div>
  );
}

// ============================================================
// AgingBody
// ============================================================
function AgingBody({
  data, sortBy, setSortBy,
}: { data: any; sortBy: 'total' | 'overdue'; setSortBy: (s: 'total' | 'overdue') => void }) {
  const totals = data.totals;

  const sortedCustomers = useMemo(() => {
    const rows = [...(data.customers ?? [])];
    if (sortBy === 'overdue') {
      rows.sort((a, b) =>
        (b.b1_30 + b.b31_60 + b.b61_90 + b.b90_plus) -
        (a.b1_30 + a.b31_60 + a.b61_90 + a.b90_plus)
      );
    } else {
      rows.sort((a, b) => b.total - a.total);
    }
    return rows;
  }, [data.customers, sortBy]);

  if (!data.customers?.length) {
    return (
      <div className="card">
        <EmptyState
          message="Nothing outstanding"
          hint="No unpaid AR balances as of this date. Great news."
        />
      </div>
    );
  }

  const overdueTotal = totals.b1_30 + totals.b31_60 + totals.b61_90 + totals.b90_plus;

  return (
    <>
      {/* KPI strip — total vs overdue is the story */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total outstanding" value={`${fmtCurrency(totals.total)}`} accent="finance" />
        <KpiTile label="Current" value={`${fmtCurrency(totals.current)}`} accent="sage" />
        <KpiTile label="Overdue" value={`${fmtCurrency(overdueTotal)}`}
          accent={overdueTotal > 0 ? 'amber' : 'default'}
          sublabel={totals.total > 0 ? `${((overdueTotal / totals.total) * 100).toFixed(0)}% of total` : undefined} />
        <KpiTile label="90+ days" value={`${fmtCurrency(totals.b90_plus)}`}
          accent={totals.b90_plus > 0 ? 'danger' : 'default'} />
      </div>

      {/* Visual aging bar */}
      <div className="card p-5 mb-4 border-slate-200">
        <div className="eyebrow text-slate-500 mb-3">Aging distribution</div>
        <StackedBucketBar totals={totals} />
        <div className="grid grid-cols-5 gap-2 mt-4">
          <BucketLegend label="Current"    value={totals.current}  color="bg-sage-500" />
          <BucketLegend label="1-30 days"  value={totals.b1_30}    color="bg-amber-400" />
          <BucketLegend label="31-60 days" value={totals.b31_60}   color="bg-amber-600" />
          <BucketLegend label="61-90 days" value={totals.b61_90}   color="bg-orange-600" />
          <BucketLegend label="90+ days"   value={totals.b90_plus} color="bg-danger" />
        </div>
      </div>

      {/* Sort toolbar */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center border-slate-200">
        <span className="eyebrow text-slate-500 px-3">Sort</span>
        <button
          onClick={() => setSortBy('overdue')}
          className={clsx(
            'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all',
            sortBy === 'overdue'
              ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
              : 'text-slate-600 hover:bg-slate-50'
          )}
        >
          Most overdue
        </button>
        <button
          onClick={() => setSortBy('total')}
          className={clsx(
            'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all',
            sortBy === 'total'
              ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
              : 'text-slate-600 hover:bg-slate-50'
          )}
        >
          Highest total
        </button>
      </div>

      {/* By-customer detail */}
      <div className="card overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Customer</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Current</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-amber-700">1-30</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-amber-700">31-60</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-orange-700">61-90</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">90+</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-700">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedCustomers.map((c: any, i: number) => (
                <tr key={c.customerId} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{c.customerName}</div>
                    <div className="text-2xs text-slate-500 mt-0.5 font-mono">
                      {c.customerEmail ?? <span className="italic">no email</span>}
                      <span className="ml-2 tabular-nums">· {c.invoices.length} invoice{c.invoices.length === 1 ? '' : 's'}</span>
                    </div>
                  </td>
                  <BucketCell value={c.current} />
                  <BucketCell value={c.b1_30} color="text-amber-700" />
                  <BucketCell value={c.b31_60} color="text-amber-800" />
                  <BucketCell value={c.b61_90} color="text-orange-800" />
                  <BucketCell value={c.b90_plus} color="text-danger" bold />
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 tabular-nums">
                    ${c.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300">
                <td className="px-4 py-3 eyebrow text-slate-700">Grand total</td>
                <BucketCell value={totals.current} foot />
                <BucketCell value={totals.b1_30} color="text-amber-800" foot />
                <BucketCell value={totals.b31_60} color="text-amber-900" foot />
                <BucketCell value={totals.b61_90} color="text-orange-900" foot />
                <BucketCell value={totals.b90_plus} color="text-danger" bold foot />
                <td className="px-4 py-3 text-right font-mono font-display font-bold text-lg text-slate-900 tabular-nums">
                  ${totals.total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}

// ============================================================
// StackedBucketBar — single-row percentage chart
// ============================================================
function StackedBucketBar({ totals }: { totals: any }) {
  const total = totals.total || 1;
  const segments = [
    { label: 'Current',    value: totals.current,  color: 'bg-sage-500' },
    { label: '1-30 days',  value: totals.b1_30,    color: 'bg-amber-400' },
    { label: '31-60 days', value: totals.b31_60,   color: 'bg-amber-600' },
    { label: '61-90 days', value: totals.b61_90,   color: 'bg-orange-600' },
    { label: '90+ days',   value: totals.b90_plus, color: 'bg-danger' },
  ];
  return (
    <div className="flex h-8 rounded-md overflow-hidden bg-slate-100">
      {segments.map((seg) =>
        seg.value > 0 ? (
          <div
            key={seg.label}
            className={clsx('h-full relative group', seg.color)}
            style={{ width: `${(seg.value / total) * 100}%` }}
            title={`${seg.label}: ${fmtCurrency(seg.value)} (${((seg.value / total) * 100).toFixed(1)}%)`}
          />
        ) : null
      )}
    </div>
  );
}

function BucketLegend({
  label, value, color,
}: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-2xs text-slate-600">
        <span className={clsx('w-2 h-2 rounded-sm', color)} />
        <span className="uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="mt-1 font-mono font-bold text-slate-900 tabular-nums">
        ${value.toFixed(2)}
      </div>
    </div>
  );
}

function BucketCell({
  value, color = 'text-slate-700', bold, foot,
}: { value: number; color?: string; bold?: boolean; foot?: boolean }) {
  const classes = clsx(
    foot ? 'px-4 py-3' : 'px-4 py-2.5',
    'text-right font-mono tabular-nums',
    value > 0 ? color : 'text-slate-300',
    (bold || foot) && value > 0 ? 'font-bold' : value > 0 ? 'font-medium' : ''
  );
  return (
    <td className={classes}>
      {value > 0 ? `${fmtCurrency(value)}` : '—'}
    </td>
  );
}

function KpiTile({
  label, value, accent = 'default', sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-slate-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
